<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
    <header class="bg-emerald-600 text-white py-6 px-4">
      <div class="max-w-3xl mx-auto">
        <a :href="backUrl" class="text-emerald-200 hover:text-white text-sm">&larr; Back</a>
        <h1 class="text-2xl font-bold mt-1">Conversation Practice</h1>
        <p v-if="scenarioName" class="text-emerald-100 text-sm mt-1">{{ scenarioName }}</p>
      </div>
    </header>

    <main class="flex-1 flex flex-col max-w-3xl mx-auto w-full px-4 py-6">
      <!-- Scenario Selection -->
      <div v-if="!conversationStarted && !starting" class="space-y-4">
        <!-- Scripted dialogues used to be promoted from here, because this was
             their only entry point. They now sit in each level's band on the
             dashboard, which is both discoverable and level-aware. -->
        <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-100">Choose a scenario</h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            v-for="scenario in scenarios"
            :key="scenario.value"
            @click="startConversation(scenario.value)"
            class="bg-white border border-gray-200 rounded-xl p-5 text-left hover:border-emerald-300 hover:shadow-sm transition-all"
          >
            <div class="text-2xl mb-2">{{ scenario.icon }}</div>
            <h3 class="font-semibold text-gray-900">{{ scenario.label }}</h3>
            <p class="text-sm text-gray-500 mt-1">{{ scenario.description }}</p>
          </button>
        </div>
      </div>

      <!-- Starting spinner -->
      <div v-else-if="starting" class="text-center py-16">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-4 border-emerald-200 border-t-emerald-600"></div>
        <p class="mt-4 text-gray-500">Starting conversation...</p>
      </div>

      <!-- Chat Interface -->
      <template v-else>
        <div class="flex-1 overflow-y-auto space-y-4 mb-4" ref="chatContainer">
          <div
            v-for="(msg, i) in messages"
            :key="i"
            :class="msg.role === 'assistant' ? 'flex justify-start' : 'flex justify-end'"
          >
            <div
              :class="[
                'max-w-[80%] rounded-2xl px-4 py-3',
                msg.role === 'assistant'
                  ? 'bg-white border border-gray-200 rounded-tl-sm'
                  : 'bg-emerald-600 text-white rounded-tr-sm'
              ]"
            >
              <div class="flex items-start gap-2">
                <!-- Replies are generated live, so there's no pre-rendered clip:
                     AudioButton falls back to browser speech for :text, and the
                     turtle slows that fallback too. -->
                <AudioButton
                  v-if="msg.role === 'assistant'"
                  :text="msg.text"
                  :show-slow="true"
                  class="shrink-0 !w-7 !h-7"
                />
                <p class="text-sm font-medium">{{ msg.text }}</p>
              </div>
              <p v-if="msg.translation && msg.role === 'assistant'"
                 class="text-xs text-gray-400 mt-1 italic">{{ msg.translation }}</p>

              <div v-if="msg.corrections && msg.corrections.length > 0" class="mt-2 bg-amber-50 rounded-lg p-2">
                <p class="text-xs font-medium text-amber-800 mb-1">Corrections:</p>
                <ul class="text-xs text-amber-700 space-y-0.5">
                  <li v-for="(correction, j) in msg.corrections" :key="j">{{ correction }}</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Typing indicator -->
          <div v-if="waitingForReply" class="flex justify-start">
            <div class="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3">
              <div class="flex gap-1">
                <div class="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style="animation-delay: 0ms"></div>
                <div class="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style="animation-delay: 150ms"></div>
                <div class="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style="animation-delay: 300ms"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Conversation complete -->
        <div v-if="conversationComplete || conversationEnded" class="text-center py-4">
          <p class="text-sm text-gray-500 mb-3 dark:text-gray-400">
            {{ conversationEnded ? endedMessage : 'Conversation complete!' }}
          </p>
          <div class="flex gap-3 justify-center">
            <button @click="resetConversation" class="bg-emerald-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-emerald-700">
              New Conversation
            </button>
            <a :href="backUrl" class="bg-white text-gray-700 border border-gray-300 px-6 py-2 rounded-lg font-medium hover:bg-gray-50">
              Back to Home
            </a>
          </div>
        </div>

        <!-- Input area -->
        <div v-else class="bg-white border-t border-gray-200 p-4 rounded-b-xl">
          <div class="flex gap-2 items-start">
            <VietnameseInput
              v-model="userInput"
              @enter="sendMessage"
              :disabled="waitingForReply"
              class="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:border-emerald-400 focus:outline-none"
              wrapper-class="flex-1"
              placeholder="Type in Vietnamese..."
            />
            <button
              @click="sendMessage"
              :disabled="!userInput.trim() || waitingForReply"
              class="bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Send
            </button>
          </div>
        </div>
      </template>
    </main>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted } from 'vue'
import { apiGet, apiPost } from '../api/client'
import VietnameseInput from './shared/VietnameseInput.vue'
import AudioButton from './shared/AudioButton.vue'

const props = defineProps({
  maxLevelUrl: { type: String, required: true },
  backUrl: { type: String, required: true },
})

const scenarios = [
  { value: 'at_market', label: 'At the Market', icon: '\u{1F3EA}', description: 'Buy fruits and vegetables from a vendor' },
  { value: 'meeting_friend', label: 'Meeting a Friend', icon: '\u{1F44B}', description: 'Meet someone for the first time at a cafe' },
  { value: 'ordering_food', label: 'Ordering Food', icon: '\u{1F35C}', description: 'Order a meal at a Vietnamese restaurant' },
  { value: 'asking_directions', label: 'Asking Directions', icon: '\u{1F5FA}', description: 'Ask a local for help finding a place' },
  { value: 'at_school', label: 'At School', icon: '\u{1F4DA}', description: 'Chat with a classmate on the first day' },
]

const maxLevel = ref(1)
const conversationStarted = ref(false)
const starting = ref(false)
const conversationId = ref(null)
const scenarioName = ref('')
const messages = ref([])
const userInput = ref('')
const waitingForReply = ref(false)
const conversationComplete = ref(false)
// Set when a turn fails (expired session, backend down). Renders the same
// restart affordance as a completed conversation.
const conversationEnded = ref(false)
const endedMessage = ref('')
const chatContainer = ref(null)
onMounted(async () => {
  try {
    const data = await apiGet(props.maxLevelUrl)
    maxLevel.value = data.max_level
  } catch (e) {
    maxLevel.value = 1
  }
})

async function scrollToBottom() {
  await nextTick()
  if (chatContainer.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight
  }
}

async function startConversation(scenario) {
  starting.value = true
  try {
    const result = await apiPost('/api/v1/conversations/start', {
      level: maxLevel.value,
      scenario: scenario,
    })

    if (result.error) {
      alert('Failed to start conversation: ' + result.error)
      starting.value = false
      return
    }

    conversationId.value = result.conversation_id
    scenarioName.value = result.scenario_description
    messages.value.push(result.message)
    conversationStarted.value = true
  } catch (e) {
    alert("We couldn't start the conversation just now. Try again in a moment.")
  } finally {
    starting.value = false
  }
  scrollToBottom()
}

async function sendMessage() {
  const text = userInput.value.trim()
  if (!text || waitingForReply.value) return

  messages.value.push({ role: 'user', text: text })
  userInput.value = ''
  waitingForReply.value = true
  scrollToBottom()

  try {
    const result = await apiPost('/api/v1/conversations/reply', {
      conversation_id: conversationId.value,
      message: text,
    })

    if (result?.error) {
      // Surface the server's message and offer a restart. Without this the
      // only escape from an expired chat was navigating away.
      conversationEnded.value = true
      endedMessage.value = result.message ||
        'This conversation timed out. Start a new one to keep practising.'
    } else {
      messages.value.push(result.message)
      if (result.conversation_complete) {
        conversationComplete.value = true
      }
    }
  } catch (e) {
    messages.value.push({ role: 'assistant', text: 'Connection error. Please try again.', translation: '' })
  } finally {
    waitingForReply.value = false
    scrollToBottom()
  }
}

function resetConversation() {
  conversationStarted.value = false
  conversationId.value = null
  scenarioName.value = ''
  messages.value = []
  userInput.value = ''
  conversationComplete.value = false
  conversationEnded.value = false
  endedMessage.value = ''
}
</script>
